import { ReactNode, useMemo } from "react";
import { useField } from "formik";
import {
  Description,
  ErrorMessage,
  Field,
  Label,
} from "@/components/catalyst/fieldset";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { InfoIcon } from "lucide-react";
import { useFormEditState } from "@/lib/Components/Form/Hooks/useFormEditState";
import { cn } from "@/lib/utils";
import { useTranslations } from "use-intl";

export type GenericFieldProps = {
  name: string;
  label: ReactNode;
  description?: ReactNode;
  className?: string;
  emptyValue?: "" | null;
  optionalLabel?: boolean;
  tooltip?: ReactNode;
  isEditable?: boolean;
  viewNode: ReactNode;
  /**
   * Pass a function to keep the input tree out of view mode — it is only
   * called when the field is being edited.
   */
  children: ReactNode | (() => ReactNode);
  autoFocus?: boolean;
};

export type InputProps = Partial<GenericFieldProps> &
  Pick<GenericFieldProps, "name" | "label">;

// Helper function to flatten error messages
const flattenError = (error: unknown): string => {
  if (!error) return "";

  if (typeof error === "string") return error;

  if (Array.isArray(error)) {
    return error
      .map((v) => {
        if (typeof v === "string") return v;
        if (typeof v === "object" && v !== null) {
          return Object.values(v as Record<string, unknown>)
            .filter((val): val is string => typeof val === "string")
            .join(", ");
        }
        return "";
      })
      .filter(Boolean)
      .join(", ");
  }

  if (typeof error === "object") {
    return Object.values(error as Record<string, unknown>)
      .filter((v): v is string => typeof v === "string")
      .join(", ");
  }

  return "";
};

export function GenericField({
  className,
  children,
  description,
  name,
  label,
  optionalLabel,
  tooltip,
  isEditable = true,
  viewNode,
}: GenericFieldProps) {
  const { isEditing } = useFormEditState();
  const [field, { error, touched }] = useField(name);

  // Development warning for undefined values
  if (import.meta.env.DEV && field.value === undefined) {
    console.warn(
      `No value was found for form key: '${name}', either the value is not specified in initialValues OR the value should be null (instead of undefined)`,
    );
  }

  // Memoize error string calculation
  const errorString = useMemo(() => flattenError(error), [error]);

  // Determine if we should show edit mode
  const showEditMode = isEditing && isEditable;
  const showError = touched && errorString;

  return (
    <Field
      className={cn("col-span-3 min-h-[60px] max-lg:col-span-full", className)}
    >
      <div className="flex space-x-1">
        <FieldLabel label={label} optionalLabel={optionalLabel} />
        {tooltip ? (
          <Tooltip delayDuration={300}>
            <TooltipTrigger tabIndex={-1} type="button">
              <InfoIcon className="ml-2 size-3" />
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        ) : null}
      </div>

      {showEditMode ? (
        <div className="relative">
          {typeof children === "function" ? children() : children}
          {showError ? (
            <ErrorMessage className="truncate" title={errorString}>
              {errorString}
            </ErrorMessage>
          ) : null}
        </div>
      ) : (
        <div className="mt-[2px] border-t border-gray-200">
          <div className="w-full pb-[5px] pt-2 text-sm text-gray-700">
            {viewNode}
          </div>
        </div>
      )}

      {description ? <Description>{description}</Description> : null}
    </Field>
  );
}

function FieldLabel({
  label,
  optionalLabel,
}: {
  label: ReactNode;
  optionalLabel?: boolean;
}) {
  const t = useTranslations("common.form");

  return (
    <Label className="flex min-w-0 items-center">
      <span className="truncate">{label}</span>
      {optionalLabel ? (
        <span className="ml-2 inline-flex shrink-0 items-center text-gray-500">
          ({t("optional")})
        </span>
      ) : null}
    </Label>
  );
}
